/**
 * MIQView Component
 * 
 * Presentational component for the Personal MetaBolicIQ Intelligence Profile tab.
 * Contains MIQ Score, Confidence Factors, Daily Check-Ins, SmartMeals, Intelligence Brief
 * 
 * All state and handlers are passed in as props from App.js.
 * 
 * Phase 1.5 Compression - SAFE STRUCTURAL EXTRACTION
 * R2 Sprint - Added Calibration Timeline, Baseline Report, Weekly Brief
 * R3 Sprint - Deterministic MIQ Score Curve (0-100 scale, Day 1 starts at 15)
 */

import React, { useState, useEffect, Component, useRef } from 'react';
import { Brain, Clock, CheckCircle2, Sparkles } from 'lucide-react';
import BloodworkSection from '../bloodwork/BloodworkSection';

// Error boundary to prevent individual sections from crashing the whole page
class SectionErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error(`[MIQView] ${this.props.section || 'Section'} crashed:`, error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '8px 12px',
          marginBottom: '8px',
          background: 'rgba(244,67,54,0.08)',
          border: '1px solid rgba(244,67,54,0.2)',
          borderRadius: '8px',
          fontSize: '10px',
          color: '#f44336',
          fontFamily: 'monospace'
        }}>
          [{this.props.section}] render error: {this.state.error?.message || 'unknown'}
        </div>
      );
    }
    return this.props.children;
  }
}
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import MIQIntelligenceBrief from './MIQIntelligenceBrief';
import SmartChoicesCard from './SmartChoicesCard';
import MIQCalibrationStatus, { CalibrationTimeline, CalibrationStatusTag, MomentumBonusIndicator } from './MIQCalibrationStatus';
import MIQBaselineModal, { BaselineReadyModal } from './MIQBaselineModal';
import MIQCalibrationBanner from './MIQCalibrationBanner';
import MIQBaselineReport from './MIQBaselineReport';
import MIQAnticipationBanner from './MIQAnticipationBanner';
import MIQTrendChart from './MIQTrendChart';
import PeptideDoseCalculator from './PeptideDoseCalculator';
import WorkoutPlanner from './WorkoutPlanner';
import { isGeniusTier, isSmartOrHigher as checkSmartOrHigher, normalizeTier } from '../../utils/tierUtils';
import { getDisplayedMIQScore, getMIQStatusLabel, getMIQStatusColor } from '../../utils/miqScoreCurve';
import { brand } from '../../styles/brand';
import { track, trackLockedCtaClicked } from '../../utils/analytics';

const { colors, gradients, typography, radius, shadows, spacing } = brand;

/**
 * CONFIDENCE FACTORS CONFIGURATION - Single Source of Truth
 * Used by both ConfidenceTracker and MIQConfidenceFactors components
 * 
 *   PROFILE: 15%
 *   ASSESSMENT: 30%
 *   MEALS: 20%
 *   CHECKINS: 15% (9 + 3 sleep + 3 steps)
 *   METABOLIC_BODY: 10% (reduced from 15)
 *   ANCESTRY: 5%
 *   LIFESTYLE_SCHEDULE: 5% (NEW - shift work context)
 *   Total weight: 100% (12 segments)
 */
export const CONFIDENCE_FACTOR_CONFIG = {
  TOTAL_SEGMENTS: 15, // Updated: +2 for sleep/steps verification
  TOTAL_FACTORS: 9, // Number of distinct factor categories (v1.2: Added Lifestyle Schedule)
  FACTORS: {
    PROFILE: { weight: 15, maxSegments: 2 },
    ASSESSMENT: { weight: 30, maxSegments: 4 },
    MEALS: { weight: 20, maxSegments: 3, threshold: 10 },
    // Checkins breakdown
    CHECKINS_COMPLETED: { weight: 9, maxSegments: 1 },
    CHECKINS_SLEEP_VERIFIED: { weight: 3, maxSegments: 1 },
    CHECKINS_STEPS_VERIFIED: { weight: 3, maxSegments: 1 },
    // Total checkins
    CHECKINS: { weight: 15, maxSegments: 2, threshold: 7 },
    METABOLIC_BODY: { weight: 10, maxSegments: 1 }, // Reduced from 15
    ANCESTRY: { weight: 5, maxSegments: 1 }, // Small, capped contribution
    LIFESTYLE_SCHEDULE: { weight: 5, maxSegments: 1 } // NEW: Shift work context
  }
};

/**
 * Calculate confidence segments from user data
 * Returns segment counts for each factor and total
 */
export const calculateConfidenceSegments = ({
  profileComplete = false,
  assessmentComplete = false,
  totalMealsLogged = 0,
  dailyCheckinsCount = 0,
  sleepVerified = false,
  stepsVerified = false,
  hasMetabolicBody = false,
  hasAncestry = false,
  hasLifestyleSchedule = false
}) => {
  const { FACTORS } = CONFIDENCE_FACTOR_CONFIG;
  
  const segments = {
    profile: profileComplete ? FACTORS.PROFILE.maxSegments : 0,
    assessment: assessmentComplete ? FACTORS.ASSESSMENT.maxSegments : 0,
    meals: Math.min(FACTORS.MEALS.maxSegments, Math.floor(totalMealsLogged / 3)),
    checkins: Math.min(FACTORS.CHECKINS.maxSegments, Math.floor(dailyCheckinsCount / 3)),
    sleepVerified: sleepVerified ? FACTORS.CHECKINS_SLEEP_VERIFIED.maxSegments : 0,
    stepsVerified: stepsVerified ? FACTORS.CHECKINS_STEPS_VERIFIED.maxSegments : 0,
    metabolicBody: hasMetabolicBody ? FACTORS.METABOLIC_BODY.maxSegments : 0,
    ancestry: hasAncestry ? FACTORS.ANCESTRY.maxSegments : 0, // Never reduces, only adds
    lifestyleSchedule: hasLifestyleSchedule ? FACTORS.LIFESTYLE_SCHEDULE.maxSegments : 0 // NEW: Shift work context
  };
  
  const total = Math.min(
    CONFIDENCE_FACTOR_CONFIG.TOTAL_SEGMENTS,
    segments.profile + segments.assessment + segments.meals + segments.checkins + segments.sleepVerified + segments.stepsVerified + segments.metabolicBody + segments.ancestry + segments.lifestyleSchedule
  );
  
  return { ...segments, total };
};

/**
 * Calculate completed factor counts from user data
 * Returns { completed: X, total: 9 } for "X of 9 factors active" displays
 * 
 * The 9 top-level factor categories are:
 * 1. Profile: has height/weight
 * 2. Assessment: intake complete
 * 3. Meals: at least 1 meal logged (progress counts)
 * 4. Check-ins: at least 1 check-in (progress counts)
 * 5. Metabolic Body: body type inferred
 * 6. SmartMeals: AI-planned meals logged
 * 7. Smart Swaps: saved recipes logged
 * 8. Ancestry: ancestry provided
 * 9. Lifestyle Schedule: work schedule type provided
 */
export const calculateFactorCounts = ({
  profileComplete = false,
  assessmentComplete = false,
  totalMealsLogged = 0,
  dailyCheckinsCount = 0,
  sleepVerified = false,
  stepsVerified = false,
  hasMetabolicBody = false,
  hasAncestry = false,
  hasLifestyleSchedule = false,
  smartMealsLogged = 0,
  savedRecipesLogged = 0
}) => {
  const { FACTORS, TOTAL_FACTORS } = CONFIDENCE_FACTOR_CONFIG;
  
  // Count factors that have contributed ANY data (not necessarily complete)
  const factorStatus = {
    profile: profileComplete,
    assessment: assessmentComplete,
    meals: totalMealsLogged >= FACTORS.MEALS.threshold,
    mealsProgress: totalMealsLogged > 0,
    checkins: dailyCheckinsCount >= FACTORS.CHECKINS.threshold,
    checkinsProgress: dailyCheckinsCount > 0,
    sleepVerified: sleepVerified,
    stepsVerified: stepsVerified,
    metabolicBody: hasMetabolicBody,
    smartMeals: smartMealsLogged >= 3,
    smartMealsProgress: smartMealsLogged > 0,
    smartSwaps: savedRecipesLogged >= 5,
    smartSwapsProgress: savedRecipesLogged > 0,
    ancestry: hasAncestry,
    lifestyleSchedule: hasLifestyleSchedule
  };
  
  // Count completed factors (fully contributing) — 9 categories
  const completed = [
    factorStatus.profile,
    factorStatus.assessment,
    factorStatus.meals,
    factorStatus.checkins,
    factorStatus.metabolicBody,
    factorStatus.smartMeals,
    factorStatus.smartSwaps,
    factorStatus.ancestry,
    factorStatus.lifestyleSchedule
  ].filter(Boolean).length;
  
  // Count active factors (any contribution, including progress)
  const active = [
    factorStatus.profile,
    factorStatus.assessment,
    factorStatus.mealsProgress || factorStatus.meals,
    factorStatus.checkinsProgress || factorStatus.checkins,
    factorStatus.metabolicBody,
    factorStatus.smartMealsProgress || factorStatus.smartMeals,
    factorStatus.smartSwapsProgress || factorStatus.smartSwaps,
    factorStatus.ancestry,
    factorStatus.lifestyleSchedule
  ].filter(Boolean).length;
  
  return { 
    completed, 
    active,
    total: TOTAL_FACTORS,
    factorStatus 
  };
};

/**
 * ConfidenceTracker Component
 * 
 * Subtle visual indicator showing MIQ confidence building over time.
 * - 12 segments max (neutral gradient: blue/purple/gray)
 * - No red/green, no percentages, no axes
 * - Animation: soft fade-in on meal log, gentle pulse on check-in
 * - NOT a performance chart - purely shows data accumulation
 * 
 * Receives pre-calculated segmentData from parent to ensure consistency.
 */
const ConfidenceTracker = ({ 
  segmentData,
  miqScore = 38 
}) => {
  const TOTAL_SEGMENTS = CONFIDENCE_FACTOR_CONFIG.TOTAL_SEGMENTS;
  const filledSegments = segmentData?.total || 0;
  
  // Track last segment for pulse animation
  const [pulseSegment, setPulseSegment] = useState(-1);
  const [prevFilledSegments, setPrevFilledSegments] = useState(filledSegments);
  
  useEffect(() => {
    // Pulse when segments increase
    if (filledSegments > prevFilledSegments) {
      setPulseSegment(filledSegments - 1);
      const timer = setTimeout(() => setPulseSegment(-1), 2000);
      setPrevFilledSegments(filledSegments);
      return () => clearTimeout(timer);
    }
    setPrevFilledSegments(filledSegments);
  }, [filledSegments, prevFilledSegments]);
  
  return (
    <div style={{ 
      marginTop: '16px',
      padding: '12px 16px',
      background: 'rgba(45, 212, 191, 0.03)',
      borderRadius: '10px',
      border: '1px solid rgba(45, 212, 191, 0.1)'
    }}>
      {/* Label */}
      <p style={{ 
        margin: '0 0 4px 0', 
        fontSize: '11px', 
        fontWeight: '600',
        color: 'var(--text-secondary)',
        letterSpacing: '0.3px'
      }}>
        Confidence building
      </p>
      <p style={{ 
        margin: '0 0 10px 0', 
        fontSize: '10px', 
        color: 'var(--text-muted, rgba(255,255,255,0.5))',
        lineHeight: '1.4'
      }}>
        MIQ confidence increases as patterns repeat — not from single actions.
      </p>
      
      {/* Segmented Progress Bar */}
      <div style={{ 
        display: 'flex', 
        gap: '3px',
        height: '8px'
      }}>
        {Array.from({ length: TOTAL_SEGMENTS }).map((_, index) => {
          const isFilled = index < filledSegments;
          const isPulsing = index === pulseSegment;
          
          // Gradient from teal to indigo (calm, intelligence, trust)
          const getSegmentColor = (idx, filled) => {
            if (!filled) return 'rgba(55, 65, 81, 0.4)';  // Muted charcoal/slate
            const progress = idx / TOTAL_SEGMENTS;
            if (progress < 0.33) return 'rgba(45, 212, 191, 0.75)';   // Teal
            if (progress < 0.66) return 'rgba(79, 170, 185, 0.75)';   // Teal-indigo blend
            return 'rgba(99, 102, 241, 0.75)';                        // Indigo
          };
          
          return (
            <div
              key={index}
              style={{
                flex: 1,
                height: '100%',
                borderRadius: '2px',
                background: getSegmentColor(index, isFilled),
                transition: 'background 0.6s ease-out, opacity 0.3s ease',
                opacity: isFilled ? 1 : 0.5,
                animation: isPulsing ? 'confidencePulse 1.5s ease-in-out' : 'none'
              }}
            />
          );
        })}
      </div>
      
      {/* Subtle explainer */}
      <p style={{ 
        margin: '8px 0 0 0', 
        fontSize: '9px', 
        color: 'var(--text-muted, rgba(255,255,255,0.4))',
        fontStyle: 'italic'
      }}>
        This reflects how much consistent data MIQ has learned from. It does not measure progress or results.
      </p>
      
      {/* CSS Animation */}
      <style>{`
        @keyframes confidencePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

/**
 * CollapsibleZone Component
 * 
 * PHASE 2: MIQ Cognitive Load Reduction
 * Collapsible section with header that expands/collapses content.
 */
const CollapsibleZone = ({ 
  title, 
  icon, 
  defaultExpanded = false, 
  children,
  badge = null
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  return (
    <div style={{ marginBottom: '16px' }}>
      {/* Header - Clickable */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 16px',
          background: isExpanded 
            ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(118, 75, 162, 0.08) 100%)'
            : 'rgba(255,255,255,0.03)',
          border: isExpanded 
            ? '1px solid rgba(102, 126, 234, 0.25)'
            : '1px solid rgba(255,255,255,0.08)',
          borderRadius: isExpanded ? '12px 12px 0 0' : '12px',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '18px' }}>{icon}</span>
          <span style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            color: 'var(--text-primary)' 
          }}>
            {title}
          </span>
          {badge && (
            <span style={{
              fontSize: '10px',
              padding: '2px 8px',
              borderRadius: '10px',
              background: 'rgba(102, 126, 234, 0.2)',
              color: '#667eea',
              fontWeight: '500'
            }}>
              {badge}
            </span>
          )}
        </div>
        <span style={{ 
          fontSize: '14px', 
          color: 'var(--text-secondary)',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease'
        }}>
          ▼
        </span>
      </button>
      
      {/* Content - Collapsible */}
      {isExpanded && (
        <div style={{
          padding: '16px',
          background: 'var(--card-bg, rgba(255,255,255,0.02))',
          border: '1px solid rgba(102, 126, 234, 0.15)',
          borderTop: 'none',
          borderRadius: '0 0 12px 12px'
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

/**
 * ScoreBreakdownCollapsible - Button-based collapsible for score breakdown
 * Replaces native <details>/<summary> which has vertical text rendering bugs on mobile
 */
const ScoreBreakdownCollapsible = ({ aiMiq }) => {
  const [isOpen, setIsOpen] = useState(false);
  const components = [
    { key: 'behavioral', label: 'Nutrition & Logging', icon: '🍎', weight: 30 },
    { key: 'goal_progress', label: 'Goal Progress', icon: '🎯', weight: 25 },
    { key: 'activity_recovery', label: 'Activity & Recovery', icon: '💪', weight: 20 },
    { key: 'profile_completeness', label: 'Profile Data', icon: '📋', weight: 15 },
    { key: 'health_awareness', label: 'Health Context', icon: '🩺', weight: 10 },
  ];
  return (
    <>
      <button
        data-testid="score-breakdown-toggle"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '0', cursor: 'pointer',
          background: 'none', border: 'none',
          color: 'var(--text-primary)', fontSize: '14px', fontWeight: '600', textAlign: 'left'
        }}
      >
        <span>Score Breakdown</span>
        <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontWeight: '400' }}>
          {isOpen ? 'tap to collapse' : 'tap to expand'}
        </span>
      </button>
      {isOpen && (
        <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {components.map(({ key, label, icon, weight }) => {
            const comp = aiMiq.score_components[key];
            if (!comp) return null;
            const value = Math.round(comp.value || 0);
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '16px', width: '24px', textAlign: 'center' }}>{icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--text-primary)' }}>{label}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{value}/100 ({weight}%)</span>
                  </div>
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${value}%`, borderRadius: '3px',
                      background: value >= 70 ? '#38ef7d' : value >= 50 ? '#ff9800' : '#f44336',
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                </div>
              </div>
            );
          })}
          {aiMiq.score_components.miq_penalties && aiMiq.score_components.miq_penalties.value < 0 && (
            <div style={{ 
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 10px', background: 'rgba(244, 67, 54, 0.06)', borderRadius: '8px'
            }}>
              <span style={{ fontSize: '16px', width: '24px', textAlign: 'center' }}>⚠️</span>
              <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '12px', color: '#f44336' }}>Nutrient Penalties</span>
                <span style={{ fontSize: '12px', color: '#f44336', fontWeight: '600' }}>{Math.round(aiMiq.score_components.miq_penalties.value)} pts</span>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

/**
 * ConfidenceFactorsCollapsible - Button-based collapsible for confidence factors
 * Replaces native <details>/<summary> which has vertical text rendering bugs on mobile
 */
const ConfidenceFactorsCollapsible = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={{ marginTop: '8px' }}>
      <button
        data-testid="confidence-factors-toggle"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '8px 0', cursor: 'pointer',
          background: 'none', border: 'none', borderTop: '1px solid rgba(102, 126, 234, 0.15)',
          color: 'var(--text-secondary)', fontSize: '12px', textAlign: 'left'
        }}
      >
        <span>{isOpen ? 'Hide' : 'View'} confidence factors</span>
        <span style={{
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease', fontSize: '10px'
        }}>▼</span>
      </button>
      {isOpen && <div style={{ paddingTop: '10px' }}>{children}</div>}
    </div>
  );
};


/**
 * ProfileCompletenessBar - Visual progress bar showing profile completion
 * Shows percentage, animated bar, and top 3 incomplete items with tappable CTAs
 * Hides entirely when all factors are complete
 */
const ProfileCompletenessBar = ({
  factorData,
  onProfileSetup,
  onAssessment,
  onMealLogging,
  onDailyCheckin,
  onSmartMeals,
  totalMealsLogged = 0,
  dailyCheckinsCount = 0,
  smartMealsLogged = 0,
  savedRecipesLogged = 0,
  isGeniusPlaite = false
}) => {
  const { completed, total, factorStatus } = factorData;
  const pct = Math.round((completed / total) * 100);

  if (completed >= total) return null; // Hide when 100%

  // Build incomplete items with actions (max 3, prioritized)
  const incompleteItems = [
    !factorStatus.profile && { label: 'Complete your profile', action: onProfileSetup, icon: '👤' },
    !factorStatus.assessment && { label: 'Finish SmartGoals setup', action: onAssessment, icon: '🎯' },
    !factorStatus.meals && { label: `Log ${Math.max(0, 10 - totalMealsLogged)} more meals`, action: onMealLogging, icon: '🍽️' },
    !factorStatus.checkins && { label: `Complete ${Math.max(0, 7 - dailyCheckinsCount)} more check-ins`, action: onDailyCheckin, icon: '📋' },
    !factorStatus.smartMeals && isGeniusPlaite && { label: `Log ${Math.max(0, 3 - smartMealsLogged)} SmartMeals`, action: onSmartMeals, icon: '🧠' },
    !factorStatus.ancestry && { label: 'Add ancestry in SmartGoals', action: onAssessment, icon: '🧬' },
    !factorStatus.lifestyleSchedule && { label: 'Set work schedule in SmartGoals', action: onAssessment, icon: '🕐' },
  ].filter(Boolean).slice(0, 3);

  const barColor = pct >= 70 ? '#38ef7d' : pct >= 40 ? '#FFD700' : '#667eea';

  return (
    <div
      data-testid="profile-completeness-bar"
      style={{
        background: 'rgba(102, 126, 234, 0.06)',
        border: '1px solid rgba(102, 126, 234, 0.15)',
        borderRadius: '12px',
        padding: '14px 16px',
        marginBottom: '12px'
      }}
    >
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
          Profile Completeness
        </span>
        <span data-testid="completeness-pct" style={{ fontSize: '13px', fontWeight: '700', color: barColor }}>
          {pct}%
        </span>
      </div>

      {/* Progress bar */}
      <div style={{
        height: '6px',
        background: 'rgba(255,255,255,0.08)',
        borderRadius: '3px',
        overflow: 'hidden',
        marginBottom: incompleteItems.length > 0 ? '12px' : '0'
      }}>
        <div
          data-testid="completeness-progress"
          style={{
            height: '100%',
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${barColor}cc, ${barColor})`,
            borderRadius: '3px',
            transition: 'width 0.6s ease'
          }}
        />
      </div>

      {/* Incomplete items with CTAs */}
      {incompleteItems.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {incompleteItems.map((item, i) => (
            <button
              key={i}
              data-testid={`completeness-action-${i}`}
              onClick={item.action}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 10px',
                background: 'rgba(102, 126, 234, 0.08)',
                border: '1px solid rgba(102, 126, 234, 0.12)',
                borderRadius: '8px',
                cursor: item.action ? 'pointer' : 'default',
                fontSize: '12px',
                color: 'var(--text-secondary)',
                textAlign: 'left',
                width: '100%',
                transition: 'background 0.2s'
              }}
            >
              <span style={{ fontSize: '14px', flexShrink: 0 }}>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.action && <span style={{ fontSize: '10px', color: '#667eea' }}>→</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


/**
 * MetabolicBodyProfileExplanation - Long-form explanation modal
 */
const MetabolicBodyProfileExplanation = ({ isOpen, onClose, hasAncestry, userTier }) => {
  if (!isOpen) return null;
  
  return (
    <>
      {/* Backdrop */}
      <div 
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
          zIndex: 9998
        }}
      />
      
      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'calc(100% - 32px)',
        maxWidth: '500px',
        maxHeight: '85vh',
        overflowY: 'auto',
        background: 'var(--card-bg, #1a1a2e)',
        borderRadius: '16px',
        padding: '24px',
        zIndex: 9999,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h2 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🏋️</span> Metabolic Body Profile
            </h2>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
              How MIQ interprets your metabolism
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              fontSize: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
        
        {/* Main explanation */}
        <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
          Your Metabolic Body Profile describes how your body tends to respond to nutrition, timing, and activity based on consistent behavioral signals — not body shape, photos, or visual analysis.
        </p>
        
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
          MIQ infers this profile over time using:
        </p>
        <ul style={{ margin: '0 0 20px 0', paddingLeft: '20px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
          <li>Your goals and timeline</li>
          <li>Logged meals and portion patterns</li>
          <li>Meal timing and eating windows</li>
          <li>Activity level and recovery demands</li>
          <li>Energy balance trends</li>
        </ul>
        
        <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: 'var(--text-primary)', fontWeight: '500', lineHeight: '1.6' }}>
          This profile helps MIQ determine <em>how</em> to guide you — not just <em>how much</em> you should eat.
        </p>
        
        {/* Why This Matters */}
        <div style={{ 
          borderTop: '1px solid rgba(255,255,255,0.1)', 
          paddingTop: '16px',
          marginBottom: '16px'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            Why This Matters
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
            People differ metabolically in three important ways:
          </p>
          
          <div style={{ marginBottom: '12px' }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
              1) Energy Handling
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              Some users respond best to tighter portion control, while others succeed with more flexibility. MIQ watches consistency, hunger patterns, and adherence over time.
            </p>
          </div>
          
          <div style={{ marginBottom: '12px' }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
              2) Timing Sensitivity
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              For some users, when they eat matters as much as what they eat. MIQ learns whether earlier meals, narrower eating windows, or flexible timing work better for you.
            </p>
            {/* Smart Timer Adherence Note - Genius Plaite Only */}
            {(userTier === 'miq' || userTier === 'genius') && (
              <p style={{ 
                margin: '8px 0 0 0', 
                fontSize: '11px', 
                color: '#4CAF50', 
                lineHeight: '1.5',
                background: 'rgba(76, 175, 80, 0.08)',
                padding: '8px 10px',
                borderRadius: '6px',
                border: '1px solid rgba(76, 175, 80, 0.2)'
              }}>
                ⏱️ <strong>Smart Timer Adherence Active:</strong> Your meal timing schedule contributes to MIQ's timing sensitivity analysis. Consistent fasting windows help MIQ understand your optimal eating patterns.
              </p>
            )}
          </div>
          
          <div style={{ marginBottom: '0' }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
              3) Recovery & Demand
            </p>
            <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              Activity level and training style affect how aggressive targets should be. MIQ adapts guidance to avoid burnout or under-fueling.
            </p>
          </div>
        </div>
        
        {/* How This Affects Your Targets */}
        <div style={{ 
          borderTop: '1px solid rgba(255,255,255,0.1)', 
          paddingTop: '16px',
          marginBottom: '16px'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            How This Affects Your Targets
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
            Your Metabolic Body Profile influences:
          </p>
          <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
            <li>Calorie targets (strict vs flexible)</li>
            <li>Macro emphasis</li>
            <li>Portion guidance</li>
            <li>Meal timing suggestions</li>
          </ul>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6', fontStyle: 'italic' }}>
            This is why two users with the same calories may receive different guidance.
          </p>
        </div>
        
        {/* MIQ Confidence Explained */}
        <div style={{ 
          borderTop: '1px solid rgba(255,255,255,0.1)', 
          paddingTop: '16px',
          marginBottom: '16px'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
            MIQ Confidence Explained
          </h3>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
            MIQ Confidence reflects how well MIQ understands you — not how well you&apos;re performing.
          </p>
          <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
            Confidence increases when:
          </p>
          <ul style={{ margin: '0 0 12px 0', paddingLeft: '20px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.8' }}>
            <li>Meal patterns repeat</li>
            <li>Timing becomes consistent</li>
            <li>Goals remain stable</li>
            <li>Behaviors align over time</li>
          </ul>
          <p style={{ margin: 0, fontSize: '13px', color: 'var(--brand-primary, #667eea)', fontWeight: '500', lineHeight: '1.6' }}>
            Low confidence means MIQ is still learning, not that you are doing anything wrong.
          </p>
        </div>
        
        {/* Genetic / Ancestry Context - Conditional */}
        {hasAncestry && (
          <div style={{ 
            borderTop: '1px solid rgba(255,255,255,0.1)', 
            paddingTop: '16px',
            marginBottom: '16px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Genetic / Ancestry Context
            </h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
              If ancestry information is provided, MIQ may reference population-level nutritional tendencies for context only, and always secondary to your actual behavior.
            </p>
            
            {/* Required Disclaimer */}
            <div style={{
              background: 'rgba(255, 193, 7, 0.1)',
              border: '1px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px'
            }}>
              <span style={{ fontSize: '16px' }}>⚠️</span>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                Ancestry and body context are used for nutritional relevance only. This is not a diagnosis or medical advice.
              </p>
            </div>
          </div>
        )}
        
        {/* Footer Note */}
        <div style={{ 
          borderTop: '1px solid rgba(255,255,255,0.1)', 
          paddingTop: '16px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
            This profile updates continuously as MIQ learns from your behavior.
          </p>
        </div>
      </div>
    </>
  );
};

/**
 * MedicalProfileSummary Component
 * Shows user's saved health data from SmartGoals at a glance:
 * medical conditions, food allergies, medications, supplements, and peptides.
 * Only renders if user has any health data saved.
 */
const MedicalProfileSummary = ({ aiNutritionGoals, onEditSmartGoals }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const intakeData = aiNutritionGoals?.intake_data || {};
  const medicalConditions = (intakeData.medical_conditions || []).filter(c => c && c !== 'none');
  const foodAllergies = (intakeData.food_allergies || []).filter(a => a && a !== 'none');
  const medications = (intakeData.medications || []).filter(m => m && m !== 'none');
  const supplements = (intakeData.supplements || []).filter(s => s && s !== 'none');
  const peptides = (intakeData.peptides || []).filter(p => p && p !== 'none' && p !== 'other_peptide');
  const injuries = (intakeData.injuries || []).filter(i => i && i !== 'none' && i !== 'other_injury');
  const injuryDetails = intakeData.injury_details || '';
  const upcomingSurgery = intakeData.upcoming_surgery || '';
  
  // Don't render if no health data
  const hasAnyData = medicalConditions.length > 0 || foodAllergies.length > 0 || 
                     medications.length > 0 || supplements.length > 0 || peptides.length > 0 ||
                     injuries.length > 0;
  if (!hasAnyData) {
    return null;
  }
  
  // Helper to format condition/allergy values into readable labels
  const formatLabel = (value) => {
    return value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // Reusable chip list
  const ChipSection = ({ label, items, bgColor, borderColor, textColor, testId }) => {
    if (items.length === 0) return null;
    return (
      <div data-testid={testId} style={{ marginBottom: '12px' }}>
        <div style={{ 
          fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', 
          marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px'
        }}>
          {label}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {items.map((item, i) => (
            <span key={i} style={{
              padding: '4px 10px', background: bgColor, border: `1px solid ${borderColor}`,
              borderRadius: '12px', fontSize: '11px', color: textColor, fontWeight: '500'
            }}>
              {formatLabel(item)}
            </span>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <div 
      data-testid="medical-profile-summary"
      style={{
        background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.06) 0%, rgba(251, 146, 60, 0.04) 100%)',
        borderRadius: '12px',
        padding: '14px 16px',
        marginBottom: '12px',
        border: '1px solid rgba(239, 68, 68, 0.15)'
      }}
    >
      <div 
        data-testid="medical-profile-header"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isExpanded ? '12px' : '0', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '16px' }}>&#127973;</span>
          <div>
            <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Health Profile
            </span>
            <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
              Used for personalized recommendations &amp; interaction checks
            </p>
          </div>
        </div>
        <button
          data-testid="edit-health-profile-btn"
          onClick={(e) => { e.stopPropagation(); onEditSmartGoals(); }}
          style={{
            background: 'rgba(102, 126, 234, 0.1)',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            borderRadius: '8px',
            padding: '6px 12px',
            color: '#667eea',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          ✏️ Edit
        </button>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {isExpanded ? '▲' : '▼'}
        </span>
      </div>
      
      {isExpanded && (<>
      <ChipSection 
        label="Medical Conditions" items={medicalConditions} testId="profile-medical-conditions"
        bgColor="rgba(239, 68, 68, 0.1)" borderColor="rgba(239, 68, 68, 0.2)" textColor="#ef4444" />
      <ChipSection 
        label="Food Allergies &amp; Intolerances" items={foodAllergies} testId="profile-food-allergies"
        bgColor="rgba(251, 146, 60, 0.1)" borderColor="rgba(251, 146, 60, 0.2)" textColor="#fb923c" />
      <ChipSection 
        label="Medications" items={medications} testId="profile-medications"
        bgColor="rgba(168, 85, 247, 0.1)" borderColor="rgba(168, 85, 247, 0.2)" textColor="#a855f7" />
      <ChipSection 
        label="Supplements" items={supplements} testId="profile-supplements"
        bgColor="rgba(34, 197, 94, 0.1)" borderColor="rgba(34, 197, 94, 0.2)" textColor="#22c55e" />
      <ChipSection 
        label="Peptides" items={peptides} testId="profile-peptides"
        bgColor="rgba(102, 126, 234, 0.1)" borderColor="rgba(102, 126, 234, 0.2)" textColor="#667eea" />
      <ChipSection 
        label="Injuries &amp; Mobility" items={injuries} testId="profile-injuries"
        bgColor="rgba(249, 115, 22, 0.1)" borderColor="rgba(249, 115, 22, 0.2)" textColor="#f97316" />
      
      {/* Injury details and upcoming surgery */}
      {(injuryDetails || upcomingSurgery) && (
        <div style={{ marginBottom: '12px' }}>
          {injuryDetails && (
            <p data-testid="profile-injury-details" style={{
              margin: '0 0 4px', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5,
              padding: '6px 10px', background: 'rgba(249, 115, 22, 0.05)', borderRadius: '6px',
              fontStyle: 'italic'
            }}>
              {injuryDetails}
            </p>
          )}
          {upcomingSurgery && (
            <p data-testid="profile-upcoming-surgery" style={{
              margin: '0', fontSize: '11px', color: '#ef4444', lineHeight: 1.5, fontWeight: '500',
              padding: '6px 10px', background: 'rgba(239, 68, 68, 0.06)', borderRadius: '6px'
            }}>
              Upcoming: {upcomingSurgery}
            </p>
          )}
        </div>
      )}
      
      {/* Helper text */}
      <p style={{ 
        margin: '0', 
        fontSize: '10px', 
        color: 'var(--text-muted)',
        fontStyle: 'italic',
        borderTop: '1px solid rgba(239, 68, 68, 0.1)',
        paddingTop: '10px'
      }}>
        Your health data is private and only used to improve AI recommendations.
      </p>
      </>)}
    </div>
  );
};

const MIQView = ({
  // Data props
  userId,
  aiMiq,
  aiNutritionGoals,
  metabolicProfile,
  userProfile,
  dailyCheckinContribution,
  dailyCheckinData,
  checkedInToday,
  todayMeals,
  totalMealsLogged,
  isPremium,
  userTier,
  miqIntakeOptions,
  expandWhatIsMiq,
  BACKEND_URL,  // Added for API calls
  
  // Action handlers
  setShowDailyCheckin,
  setShowMealPlanner,
  onOpenSmartGoals,
  setExpandWhatIsMiq,
  loadMiqIntakeOptions,
  showUpgrade,
  setCurrentView,
  setShowOnboarding,
  openCameraMode,
  loadAiNutritionGoals,
  onSmartCoachOpen,      // Opens Smart Coach modal
  onAskCoachWithPrefill, // Opens Smart Coach with a prefilled message: (prefill, insightType)
  onShowToast,  // For non-blocking toast notifications
  onWorkoutLogged, // Refresh exercise data after workout logged
  openSections, // Wheel-controlled section visibility (Set of section IDs)
}) => {
  // If openSections is undefined or empty, show all sections. Empty Set means "no filter applied" (first visit).
  const sectionVisible = (id) => !openSections || openSections.size === 0 || openSections.has(id);
  // State for Metabolic Body Profile explanation modal
  const [showMetabolicExplanation, setShowMetabolicExplanation] = useState(false);
  
  // State for Baseline Modal (R2 Sprint)
  const [showBaselineReadyModal, setShowBaselineReadyModal] = useState(false);
  const [showBaselineReport, setShowBaselineReport] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // COLLAPSED STATES - MIQ PAGE CLARITY REDESIGN
  // Default: collapsed. Expand on tap only.
  // ═══════════════════════════════════════════════════════════════════════════════
  const [intelligenceBriefExpanded, setIntelligenceBriefExpanded] = useState(false);
  const [showPeptideCalculator, setShowPeptideCalculator] = useState(false);
  const [showWorkoutPlanner, setShowWorkoutPlanner] = useState(true);
  
  // Check if user has ancestry data
  const hasAncestry = !!(aiNutritionGoals?.intake_data?.ancestry);
  
  // Check if user has work schedule data
  const hasLifestyleSchedule = !!(aiNutritionGoals?.intake_data?.work_schedule_type || userProfile?.work_schedule_type);
  const workScheduleValue = aiNutritionGoals?.intake_data?.work_schedule_type || userProfile?.work_schedule_type;
  
  // Check if metabolic body profile exists
  const hasMetabolicBody = !!(userProfile?.body_composition?.body_type || metabolicProfile?.somatotype);
  
  // Check if user has peptide data
  const userPeptides = aiNutritionGoals?.intake_data?.peptides || [];
  const hasPeptides = userPeptides.length > 0 && !userPeptides.includes('none');
  
  // Calculate factor counts using shared source of truth
  const factorData = calculateFactorCounts({
    profileComplete: !!userProfile?.onboarding_completed,
    assessmentComplete: !!aiNutritionGoals?.intake_complete,
    totalMealsLogged: totalMealsLogged || 0,
    dailyCheckinsCount: dailyCheckinContribution?.consecutive_days || 0,
    sleepVerified: dailyCheckinContribution?.sleep_verified || false,
    stepsVerified: dailyCheckinContribution?.steps_verified || false,
    hasMetabolicBody,
    hasAncestry,
    hasLifestyleSchedule,
    smartMealsLogged: aiMiq?.confidence?.smart_meals_logged || 0,
    savedRecipesLogged: aiMiq?.confidence?.saved_recipes_logged || 0
  });
  
  // Calculate segment data for tracker
  const segmentData = calculateConfidenceSegments({
    profileComplete: !!userProfile?.onboarding_completed,
    assessmentComplete: !!aiNutritionGoals?.intake_complete,
    totalMealsLogged: totalMealsLogged || 0,
    dailyCheckinsCount: dailyCheckinContribution?.consecutive_days || 0,
    sleepVerified: dailyCheckinContribution?.sleep_verified || false,
    stepsVerified: dailyCheckinContribution?.steps_verified || false,
    hasMetabolicBody,
    hasAncestry,
    hasLifestyleSchedule
  });
  
  // Use calculated values (no hardcoded fallbacks)
  const factorsActive = factorData.active;
  const factorsTotal = CONFIDENCE_FACTOR_CONFIG.TOTAL_FACTORS;
  const segmentsTotal = CONFIDENCE_FACTOR_CONFIG.TOTAL_SEGMENTS;
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // MIQ CALIBRATION STATE - From backend calibration data
  // ═══════════════════════════════════════════════════════════════════════════════
  const calibration = aiMiq?.calibration || {};
  const isCalibrating = calibration.is_calibrating !== false && !calibration.calibration_complete;
  const calibrationComplete = calibration.calibration_complete === true;
  const calibrationDay = calibration.current_day || 1;
  const calibrationDaysTotal = calibration.calibration_days_total || 30;
  const calibrationStarted = calibration.calibration_started === true;
  
  // Tier checks for gating - using centralized utilities
  const isGeniusPlaite = isGeniusTier(userTier);
  const canViewBaseline = calibrationComplete && isGeniusPlaite;
  const canViewWeeklyBrief = calibrationComplete && isGeniusPlaite;
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // MIQ SCORE CALCULATION
  // 
  // During calibration (Days 1-30): Use motivational day-based curve
  // After calibration (Day 30+): Use real backend AI MIQ score (dynamic, data-driven)
  // ═══════════════════════════════════════════════════════════════════════════════
  const miqScoreData = (() => {
    // After calibration: use the real backend score
    if (!isCalibrating && aiMiq?.ai_miq_score != null) {
      const realScore = Math.round(aiMiq.ai_miq_score);
      return {
        displayedScore: realScore,
        baseScore: realScore,
        momentumActive: (aiMiq.momentum_bonus || 0) > 0,
        momentumBonus: aiMiq.momentum_bonus || 0
      };
    }
    // During calibration: use the day-based motivational curve
    return getDisplayedMIQScore(calibrationDay);
  })();
  const { displayedScore: miqScore, baseScore: miqBaseScore, momentumActive: showMomentumBonus } = miqScoreData;
  const miqStatusLabel = getMIQStatusLabel(miqScore, isCalibrating);
  const miqStatusColor = getMIQStatusColor(miqScore, isCalibrating);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // SCORE DELTA FLOATING INDICATOR
  // Shows a brief floating "+N" or "-N" number above the gauge dot when score
  // changes (week-over-week progress reveal). Non-intrusive gamification.
  // ═══════════════════════════════════════════════════════════════════════════════
  const previousScoreRef = useRef(null);
  const [scoreDelta, setScoreDelta] = useState(null);
  
  useEffect(() => {
    const prev = previousScoreRef.current;
    // Only trigger if we have a prior score AND it's materially different
    if (prev !== null && prev !== miqScore && Math.abs(miqScore - prev) >= 1) {
      const delta = miqScore - prev;
      setScoreDelta(delta);
      const t = setTimeout(() => setScoreDelta(null), 2800);
      previousScoreRef.current = miqScore;
      return () => clearTimeout(t);
    }
    previousScoreRef.current = miqScore;
  }, [miqScore]);
  
  // Model Confidence % (separate from MIQ Score - backend metric)
  // Only used for internal confidence factors display, NOT the main score
  const modelConfidence = aiMiq?.confidence?.overall || aiMiq?.confidence?.percentage || 0;
  
  // Baseline Ready Modal removed - no longer showing "Your MIQ Baseline is Ready" on Day 30
  
  // Track MIQ view
  useEffect(() => {
    track('miq_viewed', { 
      calibration_day: calibrationDay,
      is_calibrating: isCalibrating,
      miq_score: miqScore,
      model_confidence: modelConfidence,
      is_genius: isGeniusPlaite
    });
  }, []);
  
  // Listen for openBaselineReport event from Home view banner
  useEffect(() => {
    const handleOpenBaseline = () => {
      if (calibrationComplete && isGeniusPlaite) {
        setShowBaselineReport(true);
      }
    };
    window.addEventListener('openBaselineReport', handleOpenBaseline);
    return () => window.removeEventListener('openBaselineReport', handleOpenBaseline);
  }, [calibrationComplete, isGeniusPlaite]);
  
  return (
    <div className="miq-view" style={{ padding: '0 16px' }}>
      {/* Baseline Ready Modal removed - no longer used */}
      
      {/* NEW: Comprehensive Baseline Report (Day 30) */}
      <MIQBaselineReport
        isOpen={showBaselineReport}
        onClose={() => setShowBaselineReport(false)}
        userId={userId}
        onNavigateToWeeklyBriefs={() => {
          setIntelligenceBriefExpanded(true);
          // Scroll to the Weekly Brief section after a short delay
          setTimeout(() => {
            const briefEl = document.querySelector('[data-testid="weekly-intelligence-brief"]');
            if (briefEl) briefEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 200);
        }}
      />
      
      {/* Metabolic Body Profile Explanation Modal */}
      <MetabolicBodyProfileExplanation 
        isOpen={showMetabolicExplanation}
        onClose={() => setShowMetabolicExplanation(false)}
        hasAncestry={hasAncestry}
        userTier={userTier}
      />
      
      {/* NEW: MIQ Anticipation Banner - Days 25-30 countdown with previews */}
      <MIQAnticipationBanner
        userId={userId}
        calibrationDay={calibrationDay}
        isGeniusTier={isGeniusPlaite}
        onNavigateToMIQ={() => {/* Already on MIQ view */}}
        onViewBaseline={() => setShowBaselineReport(true)}
        onUpgrade={() => showUpgrade?.('miq_baseline')}
        onLogMeal={() => {/* Navigate to meal logging */}}
        onCheckIn={() => {/* Navigate to daily check-in */}}
      />
      
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* HERO SECTION - Personal MetaBolicIQ Intelligence Profile */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      <div style={{
        position: 'relative',
        background: `radial-gradient(ellipse 80% 60% at 50% 0%, ${colors.accentDim} 0%, transparent 70%), rgba(255,255,255,0.02)`,
        border: `1px solid ${colors.stroke}`,
        borderRadius: radius.xl,
        padding: '28px 20px 24px',
        marginBottom: '20px',
        textAlign: 'center',
        overflow: 'hidden',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        boxShadow: `0 4px 32px rgba(0,0,0,0.25), 0 0 60px ${colors.accentDim}`,
      }}>
        {/* Subtle compass ring glow in the background */}
        <div style={{
          position: 'absolute',
          top: '-60px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '260px',
          height: '260px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colors.accentDim} 0%, transparent 65%)`,
          pointerEvents: 'none',
          zIndex: 0,
        }} />

        {/* MIQ Header */}
        <div style={{ marginBottom: '16px', position: 'relative', zIndex: 1 }}>
          <p style={{
            margin: '0 0 14px 0',
            fontSize: '10px',
            color: colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.24em',
            fontFamily: typography.headingFont,
            fontWeight: '600',
          }}>
            Personal MetaBolicIQ Profile
          </p>
          
          {/* Calibration Status Tag */}
          {isCalibrating && calibrationStarted && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '5px 12px',
              background: 'rgba(245, 158, 11, 0.10)',
              border: '1px solid rgba(245, 158, 11, 0.25)',
              borderRadius: radius.pill,
              marginBottom: '12px',
              backdropFilter: 'blur(8px)',
            }}>
              <Clock size={11} style={{ color: colors.warning }} />
              <span style={{
                fontSize: '10px',
                color: colors.warning,
                fontFamily: typography.headingFont,
                fontWeight: '600',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}>
                Calibration in Progress
              </span>
            </div>
          )}
          
          {calibrationComplete && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '5px 12px',
              background: colors.freeDim,
              border: `1px solid ${colors.freeBorder}`,
              borderRadius: radius.pill,
              marginBottom: '12px',
              backdropFilter: 'blur(8px)',
            }}>
              <CheckCircle2 size={11} style={{ color: colors.free }} />
              <span style={{
                fontSize: '10px',
                color: colors.free,
                fontFamily: typography.headingFont,
                fontWeight: '600',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}>
                Calibration Complete
              </span>
            </div>
          )}
          
          {/* Circular Gauge */}
          <div style={{ position: 'relative', width: '200px', height: '110px', margin: '0 auto' }}>
            <style>{`
              @keyframes miqDotPulse {
                0% { r: 7; opacity: 0.9; }
                50% { r: 14; opacity: 0; }
                100% { r: 7; opacity: 0; }
              }
              @keyframes miqDotBreath {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.08); }
              }
              @keyframes miqScoreDeltaFloat {
                0% { transform: translate(-50%, 0) scale(0.6); opacity: 0; }
                15% { transform: translate(-50%, -8px) scale(1); opacity: 1; }
                75% { transform: translate(-50%, -30px) scale(1); opacity: 1; }
                100% { transform: translate(-50%, -44px) scale(0.9); opacity: 0; }
              }
            `}</style>
            
            {/* Score Delta Floating Indicator — week-over-week progress reveal */}
            {scoreDelta !== null && (() => {
              const pct = miqScore / 100;
              // Position over the indicator dot in the SVG's local coord space
              const dotX = (90 + 80 * Math.cos(Math.PI - pct * Math.PI)) / 180 * 100;
              const dotY = (90 - 80 * Math.sin(Math.PI - pct * Math.PI)) / 100 * 100;
              const isPositive = scoreDelta > 0;
              return (
                <div
                  data-testid="miq-score-delta"
                  style={{
                    position: 'absolute',
                    left: `${dotX}%`,
                    top: `${dotY}%`,
                    transform: 'translate(-50%, -8px)',
                    pointerEvents: 'none',
                    animation: 'miqScoreDeltaFloat 2.8s cubic-bezier(0.4, 0, 0.2, 1) forwards',
                    padding: '4px 10px',
                    borderRadius: radius.pill,
                    background: isPositive ? colors.freeDim : 'rgba(239, 68, 68, 0.14)',
                    border: `1px solid ${isPositive ? colors.freeBorder : 'rgba(239, 68, 68, 0.3)'}`,
                    color: isPositive ? colors.free : colors.error,
                    fontSize: '12px',
                    fontWeight: '700',
                    fontFamily: typography.monoFont,
                    letterSpacing: '-0.02em',
                    textShadow: `0 0 12px ${isPositive ? colors.free : colors.error}`,
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    whiteSpace: 'nowrap',
                    zIndex: 5,
                  }}
                >
                  {isPositive ? '+' : ''}{scoreDelta}
                </div>
              );
            })()}
            
            <svg viewBox="0 0 180 100" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
              {/* Background arc */}
              <path
                d="M 10 90 A 80 80 0 0 1 170 90"
                fill="none"
                stroke="rgba(255,255,255,0.06)"
                strokeWidth="10"
                strokeLinecap="round"
              />
              {/* Gradient arc - rose → gold → emerald (brand macro palette) */}
              <defs>
                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor={colors.protein} />
                  <stop offset="50%" stopColor={colors.carbs} />
                  <stop offset="100%" stopColor={colors.free} />
                </linearGradient>
                <filter id="scoreGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              <path
                d="M 10 90 A 80 80 0 0 1 170 90"
                fill="none"
                stroke="url(#scoreGradient)"
                strokeWidth="10"
                strokeLinecap="round"
                strokeDasharray={`${miqScore * 2.5}, 250`}
                filter="url(#scoreGlow)"
                style={{ transition: 'stroke-dasharray 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)' }}
              />
              {/* Pulsing outer ring — "heartbeat" around the indicator dot */}
              <circle
                cx={90 + 80 * Math.cos(Math.PI - (miqScore / 100) * Math.PI)}
                cy={90 - 80 * Math.sin(Math.PI - (miqScore / 100) * Math.PI)}
                r="7"
                fill="none"
                stroke={colors.accent}
                strokeWidth="1.5"
                style={{
                  transformOrigin: 'center',
                  animation: 'miqDotPulse 2.2s ease-out infinite',
                }}
              />
              {/* Score indicator dot */}
              <circle
                cx={90 + 80 * Math.cos(Math.PI - (miqScore / 100) * Math.PI)}
                cy={90 - 80 * Math.sin(Math.PI - (miqScore / 100) * Math.PI)}
                r="7"
                fill={colors.accent}
                stroke="#fff"
                strokeWidth="2.5"
                style={{
                  filter: `drop-shadow(0 0 6px ${colors.accent})`,
                  transformBox: 'fill-box',
                  transformOrigin: 'center',
                  animation: 'miqDotBreath 3.2s ease-in-out infinite',
                }}
              />
            </svg>
          </div>
          
          {/* MIQ Score Number - Deterministic day-based curve (0-100 scale) */}
          <div style={{ marginTop: '-14px' }}>
            <span style={{
              fontSize: '56px',
              fontWeight: '300',
              fontFamily: typography.monoFont,
              letterSpacing: '-0.04em',
              background: gradients.textAccent,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              {miqScore}
            </span>
            <p style={{
              margin: '2px 0 0 0',
              fontSize: '13px',
              fontWeight: '600',
              fontFamily: typography.headingFont,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: miqStatusColor,
            }}>
              {miqStatusLabel}
            </p>
          </div>
          
          {/* MIQ Calibration Progress Counter */}
          {calibrationStarted && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 14px',
              background: colors.accentDim,
              border: `1px solid ${colors.accentBorder}`,
              borderRadius: radius.pill,
              marginTop: '14px',
              backdropFilter: 'blur(8px)',
            }}>
              <Brain size={12} style={{ color: colors.accent }} />
              <span style={{
                fontSize: '10px',
                color: colors.accent,
                fontFamily: typography.headingFont,
                fontWeight: '600',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}>
                {isCalibrating
                  ? `Day ${calibrationDay} of ${calibrationDaysTotal}`
                  : `Baseline · ${factorsActive}/${factorsTotal} Factors`
                }
              </span>
            </div>
          )}
          
          {/* Calibration Messaging - During first 30 days */}
          {isCalibrating && calibrationStarted && (
            <div style={{
              marginTop: '16px',
              padding: '14px 16px',
              background: 'rgba(255,255,255,0.03)',
              borderRadius: radius.lg,
              border: `1px solid ${colors.stroke}`,
              textAlign: 'left',
              backdropFilter: 'blur(12px)',
            }}>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '12px',
                color: colors.text,
                fontWeight: '500',
                fontFamily: typography.headingFont,
                letterSpacing: '-0.005em',
              }}>
                Your MetaBolicIQ profile is being established.
              </p>
              <p style={{
                margin: 0,
                fontSize: '11px',
                color: colors.textSecondary,
                lineHeight: '1.55',
                fontFamily: typography.fontFamily,
              }}>
                MIQ learns your metabolism over ~30 days of consistent data to build a personalized baseline.
              </p>
            </div>
          )}
          
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* VISUAL CONFIDENCE TRACKER - Subtle segmented progress indicator */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          <ConfidenceTracker 
            segmentData={segmentData}
            miqScore={miqScore}
          />
          
          {/* Momentum Bonus Indicator (R2 Sprint) - Shows +10 badge when active */}
          {showMomentumBonus && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              marginTop: '12px' 
            }}>
              <MomentumBonusIndicator 
                baseConfidence={miqBaseScore} 
                showBonus={showMomentumBonus} 
              />
            </div>
          )}
          
          {/* Product Message - MIQ Tab */}
          <p style={{
            margin: '14px 0 0 0',
            fontSize: '11px',
            color: colors.mutedText,
            fontStyle: 'italic',
            fontFamily: typography.fontFamily,
            lineHeight: '1.5',
          }}>
            Understanding, not performance — more data helps MIQ learn you.
          </p>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* CALIBRATION TIMELINE (R2 Sprint) - Shows 4-week progress */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {calibrationStarted && isCalibrating && (
        <CalibrationTimeline 
          dayNumber={calibrationDay}
          isGeniusTier={isGeniusPlaite}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* AI METABOLIC GUIDANCE - Personalized insights from AI MIQ analysis */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      
      {sectionVisible('metabolic-guidance') && (
      <SectionErrorBoundary section="AI Guidance">
      {aiMiq?.insights && aiMiq.insights.length > 0 && aiMiq.insights[0]?.type !== 'welcome' && (
        <div className="card" data-testid="miq-ai-guidance-section" style={{ marginBottom: '16px' }}>
          <h3 style={{
            margin: '0 0 14px 0',
            fontSize: '11px',
            fontFamily: typography.headingFont,
            fontWeight: '600',
            color: colors.textSecondary,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Sparkles size={13} style={{ color: colors.accent }} />
            Your Metabolic Guidance
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {aiMiq.insights.map((insight, idx) => (
              <div 
                key={idx}
                data-testid={`miq-insight-${insight?.type || idx}`}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  padding: '12px 14px',
                  borderRadius: '10px',
                  background: insight?.priority === 'positive' 
                    ? 'rgba(56, 239, 125, 0.08)' 
                    : insight?.priority === 'guidance' 
                    ? 'rgba(102, 126, 234, 0.08)'
                    : insight?.priority === 'suggestion'
                    ? 'rgba(255, 152, 0, 0.08)'
                    : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${
                    insight?.priority === 'positive' 
                      ? 'rgba(56, 239, 125, 0.15)' 
                      : insight?.priority === 'guidance'
                      ? 'rgba(102, 126, 234, 0.15)'
                      : insight?.priority === 'suggestion'
                      ? 'rgba(255, 152, 0, 0.15)'
                      : 'rgba(255,255,255,0.06)'
                  }`
                }}
              >
                <span style={{ fontSize: '20px', flexShrink: 0, marginTop: '1px' }}>{insight?.icon || '💡'}</span>
                <div>
                  <p style={{ margin: '0 0 3px 0', fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    {insight?.title || 'Insight'}
                  </p>
                  <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                    {insight?.message || ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
          
          {/* MIQ Penalty Warnings - Show if any active */}
          {aiMiq?.miq_penalties?.warnings && aiMiq.miq_penalties.warnings.length > 0 && (
            <div style={{ marginTop: '12px', padding: '10px 12px', background: 'rgba(244, 67, 54, 0.06)', borderRadius: '8px', border: '1px solid rgba(244, 67, 54, 0.12)' }}>
              <p style={{ margin: '0 0 6px 0', fontSize: '11px', fontWeight: '600', color: '#f44336', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Today's Flags
              </p>
              {aiMiq.miq_penalties.warnings.map((warning, idx) => (
                <p key={idx} style={{ margin: idx === aiMiq.miq_penalties.warnings.length - 1 ? 0 : '0 0 4px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  {typeof warning === 'string' ? `• ${warning}` : `• ${warning?.message || warning?.nutrient || JSON.stringify(warning)}`}
                </p>
              ))}
            </div>
          )}

          {/* Ancestry Context - Show if user has provided ancestry data */}
          {aiMiq?.ancestry_context?.has_ancestry && (
            <div data-testid="miq-ancestry-context" style={{ marginTop: '12px', padding: '10px 12px', background: 'rgba(102, 126, 234, 0.06)', borderRadius: '8px', border: '1px solid rgba(102, 126, 234, 0.12)' }}>
              <p style={{ margin: '0 0 4px 0', fontSize: '11px', fontWeight: '600', color: '#667eea', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Ancestry Context Active
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                {aiMiq.ancestry_context.summary || 'Your ancestry data is being used to personalize metabolic guidance.'}
              </p>
            </div>
          )}
        </div>
      )}
      </SectionErrorBoundary>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* HEALTH INTELLIGENCE AGENT - AI-powered health companion */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {sectionVisible('metabolic-guidance') && (
      <div 
        data-testid="health-intelligence-agent"
        style={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(168, 85, 247, 0.08) 100%)',
          borderRadius: '16px',
          padding: '16px',
          marginBottom: '16px',
          border: '1px solid rgba(99, 102, 241, 0.25)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{
          position: 'absolute',
          top: '-20px',
          right: '-20px',
          width: '80px',
          height: '80px',
          background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', position: 'relative' }}>
          <div style={{
            width: '36px',
            height: '36px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
          }}>
            <span style={{ fontSize: '18px', lineHeight: 1 }}>&#129504;</span>
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
              Health Intelligence Agent
            </h3>
            <p style={{ margin: '1px 0 0', fontSize: '11px', color: '#a78bfa', fontWeight: '500' }}>
              AI-powered &bull; Personalized to your profile
            </p>
          </div>
        </div>
        
        <p style={{ 
          margin: '0 0 10px', 
          fontSize: '12px', 
          color: 'var(--text-secondary)', 
          lineHeight: '1.5',
          position: 'relative'
        }}>
          Intelligence-driven guidance on nutrition, supplements, exercise, peptides, and health optimization.
        </p>
        
        <div style={{ display: 'flex', gap: '6px', marginBottom: '10px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {[
            { label: 'Nutrition', prefill: 'How does my ancestry and body composition affect my nutritional needs? What should I prioritize?' },
            { label: 'Supplements', prefill: 'Review my current supplement stack. What am I missing and what interactions should I know about?' },
            { label: 'Exercise', prefill: 'Design a training approach based on my goals, body type, and any health considerations in my profile' },
            { label: 'Peptides', prefill: 'Analyze my peptide protocol — optimize timing, dosing, and stacking based on my goals' },
            { label: 'Health Optimization', prefill: 'Based on my full profile — medications, supplements, conditions, and goals — what factors, interactions, and optimizations should I be aware of?' }
          ].map((topic) => (
            <button
              key={topic.label}
              data-testid={`coach-topic-${topic.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => {
                if (onAskCoachWithPrefill) {
                  onAskCoachWithPrefill(topic.prefill, 'smart_coach');
                } else if (onSmartCoachOpen) {
                  onSmartCoachOpen();
                }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '5px 12px',
                background: 'rgba(99, 102, 241, 0.1)',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                borderRadius: '20px',
                color: '#c4b5fd',
                fontSize: '11px',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0
              }}
            >
              {topic.label}
            </button>
          ))}
        </div>
        <p style={{
          margin: '0 0 10px',
          fontSize: '10px',
          color: 'rgba(255,255,255,0.3)',
          textAlign: 'center',
          fontStyle: 'italic'
        }}>
          Or ask anything — your agent knows your full health profile
        </p>
        
        <button
          data-testid="open-health-agent-btn"
          onClick={() => onSmartCoachOpen && onSmartCoachOpen()}
          style={{
            width: '100%',
            padding: '11px',
            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
            border: 'none',
            borderRadius: '10px',
            color: 'white',
            fontWeight: '600',
            fontSize: '13px',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.3)',
            letterSpacing: '0.3px'
          }}
        >
          Chat with Your Health Intelligence Agent
        </button>
      </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* WORKOUT PLANNER - Elevated for maximum visibility */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {sectionVisible('workout-planner') && (
      <div 
        data-testid="workout-section"
        style={{
          background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(239, 68, 68, 0.06) 100%)',
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '16px',
          border: '1px solid rgba(249, 115, 22, 0.2)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <div style={{
          position: 'absolute',
          bottom: '-30px',
          left: '-30px',
          width: '120px',
          height: '120px',
          background: 'radial-gradient(circle, rgba(249, 115, 22, 0.1) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />

        <div 
          data-testid="workout-section-header"
          onClick={() => setShowWorkoutPlanner(!showWorkoutPlanner)}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            cursor: 'pointer'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
            }}>
              <span style={{ fontSize: '22px', lineHeight: 1 }}>&#127947;</span>
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>
                Workout Planner
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                AI-personalized &bull; Injury-aware &bull; CrossFit WODs
              </p>
            </div>
          </div>
          <div style={{
            background: showWorkoutPlanner ? 'rgba(249, 115, 22, 0.2)' : 'rgba(249, 115, 22, 0.1)',
            borderRadius: '8px',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#f97316',
            transition: 'all 0.2s'
          }}>
            {showWorkoutPlanner ? '▲' : '▼'}
          </div>
        </div>

        {showWorkoutPlanner && (
          <div style={{ marginTop: '16px', borderTop: '1px solid rgba(249, 115, 22, 0.15)', paddingTop: '16px' }}>
            <WorkoutPlanner
              userId={userId}
              onWorkoutLogged={onWorkoutLogged}
              onAskCoach={(prefillMessage) => {
                if (onAskCoachWithPrefill) {
                  onAskCoachWithPrefill(prefillMessage, 'smart_coach');
                } else if (onSmartCoachOpen) {
                  onSmartCoachOpen();
                }
              }}
            />
          </div>
        )}
      </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* BLOODWORK & BIOMARKERS (Genius-only) */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {sectionVisible('bloodwork') && (
        <SectionErrorBoundary section="Bloodwork">
          <BloodworkSection
            userId={userId}
            isPremium={isPremium}
            userTier={userTier}
            onAskCoach={(question) => {
              if (onAskCoachWithPrefill) {
                onAskCoachWithPrefill(question, 'bloodwork');
              }
            }}
          />
        </SectionErrorBoundary>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* PEPTIDE DOSE CALCULATOR */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {sectionVisible('peptide-calc') && hasPeptides && (
        <div 
          data-testid="peptide-section"
          style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.06) 0%, rgba(147, 51, 234, 0.04) 100%)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
            border: '1px solid rgba(102, 126, 234, 0.15)'
          }}
        >
          <div 
            data-testid="peptide-section-header"
            onClick={() => setShowPeptideCalculator(!showPeptideCalculator)}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              cursor: 'pointer'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px' }}>&#128137;</span>
              <div>
                <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                  Peptide Dose Calculator
                </span>
                <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  {userPeptides.filter(p => p !== 'none' && p !== 'other_peptide').length} peptide{userPeptides.filter(p => p !== 'none' && p !== 'other_peptide').length !== 1 ? 's' : ''} in your stack
                </p>
              </div>
            </div>
            <div style={{
              background: 'rgba(102, 126, 234, 0.1)',
              borderRadius: '8px',
              padding: '6px 12px',
              fontSize: '11px',
              fontWeight: '600',
              color: '#667eea',
              transition: 'transform 0.2s'
            }}>
              {showPeptideCalculator ? 'Collapse' : 'Open'}
            </div>
          </div>

          {showPeptideCalculator && (
            <div style={{ marginTop: '14px', borderTop: '1px solid rgba(102, 126, 234, 0.1)', paddingTop: '14px' }}>
              <PeptideDoseCalculator
                userId={userId}
                userPeptides={userPeptides}
                onAskCoach={(prefillMessage) => {
                  if (onAskCoachWithPrefill) {
                    onAskCoachWithPrefill(prefillMessage, 'smart_coach');
                  } else if (onSmartCoachOpen) {
                    onSmartCoachOpen();
                  }
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* HEALTH PROFILE - Reference while configuring tools above */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {sectionVisible('health-profile') && (
      <>
      <MedicalProfileSummary 
        aiNutritionGoals={aiNutritionGoals}
        onEditSmartGoals={async () => {
          if (loadMiqIntakeOptions) loadMiqIntakeOptions();
          if (loadAiNutritionGoals && userId) {
            try { await loadAiNutritionGoals(userId); } catch (e) { console.error(e); }
          }
          setTimeout(() => { onOpenSmartGoals && onOpenSmartGoals(); }, 100);
        }}
      />

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* SMART CHOICES TRACKER - Cumulative calorie savings from smart swaps */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      <SmartChoicesCard 
        userId={userId}
        userTier={userTier}
        onShowToast={onShowToast}
      />
      </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* 2️⃣ MIQ CONFIDENCE SCORE (Visible to all tiers) */}
      {/* MIQ Confidence reflects how complete and reliable your data is */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {sectionVisible('score-breakdown') && (
      <>
      <SectionErrorBoundary section="Confidence Score">
      <div 
        style={{
          background: 'rgba(102, 126, 234, 0.06)',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '12px',
          border: '1px solid rgba(102, 126, 234, 0.15)'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: calibrationComplete ? '0' : '8px' }}>
          <span style={{ fontSize: '16px' }}>🎯</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
              MIQ Confidence Score
            </span>
            <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
              {calibrationComplete 
                ? `Calibrated — ${factorsActive} of ${factorsTotal} factors active`
                : 'MIQ Confidence reflects how complete and reliable your data is.'
              }
            </p>
          </div>
          {calibrationComplete && (
            <span style={{ fontSize: '11px', color: 'rgba(56, 239, 125, 0.8)', fontWeight: '600', whiteSpace: 'nowrap' }}>
              ✓ Complete
            </span>
          )}
        </div>
        
        {!calibrationComplete && (
          <p style={{ margin: '0 0 10px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
            {factorsActive} of {factorsTotal} factors active
          </p>
        )}
        
        {/* Collapse checklist when calibration is complete */}
        {calibrationComplete ? (
          <ConfidenceFactorsCollapsible>
              <MIQConfidenceFactors 
            userProfile={userProfile}
            aiNutritionGoals={aiNutritionGoals}
            metabolicProfile={metabolicProfile}
            totalMealsLogged={totalMealsLogged}
            dailyCheckinContribution={dailyCheckinContribution}
            onProfileSetup={() => setShowOnboarding && setShowOnboarding(true)}
            onAssessment={async () => {
              if (loadMiqIntakeOptions) loadMiqIntakeOptions();
              if (loadAiNutritionGoals && userId) {
                try { await loadAiNutritionGoals(userId); } catch (e) { console.error(e); }
              }
              setTimeout(() => { onOpenSmartGoals && onOpenSmartGoals(); }, 100);
            }}
            onMealLogging={() => setCurrentView && setCurrentView('home')}
            onDailyCheckin={() => setShowDailyCheckin && setShowDailyCheckin(true)}
            aiMiq={aiMiq}
            userTier={userTier}
            showUpgrade={showUpgrade}
            setShowMealPlanner={setShowMealPlanner}
          />
          </ConfidenceFactorsCollapsible>
        ) : (
          <div style={{ paddingTop: '10px', borderTop: '1px solid rgba(102, 126, 234, 0.15)' }}>
            <MIQConfidenceFactors 
              userProfile={userProfile}
              aiNutritionGoals={aiNutritionGoals}
              metabolicProfile={metabolicProfile}
              totalMealsLogged={totalMealsLogged}
              dailyCheckinContribution={dailyCheckinContribution}
              onProfileSetup={() => setShowOnboarding && setShowOnboarding(true)}
              onAssessment={async () => {
                if (loadMiqIntakeOptions) loadMiqIntakeOptions();
                if (loadAiNutritionGoals && userId) {
                  try { await loadAiNutritionGoals(userId); } catch (e) { console.error(e); }
                }
                setTimeout(() => { onOpenSmartGoals && onOpenSmartGoals(); }, 100);
              }}
              onMealLogging={() => setCurrentView && setCurrentView('home')}
              onDailyCheckin={() => setShowDailyCheckin && setShowDailyCheckin(true)}
              aiMiq={aiMiq}
              userTier={userTier}
              showUpgrade={showUpgrade}
              setShowMealPlanner={setShowMealPlanner}
            />
          </div>
        )}
      </div>
      </SectionErrorBoundary>

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* SCORE BREAKDOWN */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      <SectionErrorBoundary section="Score Breakdown">
      {aiMiq?.score_components && !aiMiq?.locked && (
        <div className="card" data-testid="miq-score-breakdown" style={{ marginBottom: '16px' }}>
          <ScoreBreakdownCollapsible aiMiq={aiMiq} />
        </div>
      )}
      </SectionErrorBoundary>
      </>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* 3️⃣ 30-DAY INTELLIGENCE REPORT (Post-Calibration, Genius Users) */}
      {/* Visible card to open report + view previous reports */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {sectionVisible('reports') && calibrationComplete && isGeniusPlaite && (
        <div 
          data-testid="intelligence-report-card"
          style={{
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(147, 51, 234, 0.06) 100%)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
            border: '1px solid rgba(102, 126, 234, 0.2)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <span style={{ fontSize: '16px' }}>📋</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                30-Day Intelligence Report
              </span>
              <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
                Comprehensive metabolic analysis refreshed every 30 days
              </p>
            </div>
          </div>
          
          <button 
            data-testid="view-intelligence-report-btn"
            onClick={() => setShowBaselineReport(true)}
            style={{
              width: '100%',
              padding: '11px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer',
              marginBottom: '0'
            }}
          >
            View Latest Report
          </button>
        </div>
      )}

      {/* 3️⃣ PERSONAL MIQ BASELINE PROFILE (Post-Day-30, Tier-Gated) */}
      {/* Shows locked card for non-Genius users AFTER calibration completes */}
      {calibrationComplete && !isGeniusPlaite && (
        <div 
          style={{
            background: 'linear-gradient(135deg, rgba(147, 51, 234, 0.08) 0%, rgba(102, 126, 234, 0.05) 100%)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '12px',
            border: '1px solid rgba(147, 51, 234, 0.2)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <span style={{ fontSize: '20px' }}>🔒</span>
            <div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                Personal MetaBolicIQ Intelligence Profile
              </span>
            </div>
          </div>
          
          <p style={{ 
            margin: '0 0 12px 0', 
            fontSize: '12px', 
            color: 'var(--text-secondary)',
            lineHeight: '1.5'
          }}>
            Your personalized metabolic baseline report is now available. Upgrade to Genius Plaite to unlock your Personal MetaBolicIQ Intelligence Profile, MIQ Smart Coach, and ongoing Weekly MetaBolicIQ Intelligence Briefs.
          </p>
          
          <button 
            onClick={() => {
              trackLockedCtaClicked({
                feature: 'miq_baseline_report',
                tier: normalizeTier(userTier),
                source_view: 'miq_tab',
                reason: 'tier_locked',
                day_in_trial: calibrationDay || null
              });
              showUpgrade && showUpgrade('genius');
            }}
            style={{
              width: '100%',
              padding: '12px',
              background: 'linear-gradient(135deg, #9333ea 0%, #667eea 100%)',
              border: 'none',
              borderRadius: '10px',
              color: 'white',
              fontWeight: '600',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            Upgrade to Genius Plaite
          </button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* 4️⃣ WEEKLY METABOLICIQ INTELLIGENCE BRIEF */}
      {/* Post-Day-30: Genius users see full brief, others see locked card */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {sectionVisible('reports') && calibrationComplete && canViewWeeklyBrief && (
        <div 
          data-testid="weekly-intelligence-brief"
          onClick={() => setIntelligenceBriefExpanded(!intelligenceBriefExpanded)}
          style={{
            background: 'rgba(118, 75, 162, 0.06)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
            cursor: 'pointer',
            border: '1px solid rgba(118, 75, 162, 0.15)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px' }}>📊</span>
              <div>
                <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  Weekly MetaBolicIQ Intelligence Brief
                </span>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Based on your Personal MIQ baseline, MIQ now delivers ongoing adaptive metabolic insights.
                </p>
              </div>
            </div>
            <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{intelligenceBriefExpanded ? '▲' : '▼'}</span>
          </div>
          
          {/* Expanded: Show Weekly Intelligence Brief */}
          {intelligenceBriefExpanded && (
            <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid rgba(118, 75, 162, 0.15)' }}
                 onClick={(e) => e.stopPropagation()}>
              
              {/* Score Movement Banner */}
              {aiMiq?.latest_brief?.score_delta !== undefined && aiMiq?.latest_brief?.score_delta !== 0 && (
                <div data-testid="weekly-brief-score-delta" style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '10px 16px',
                  marginBottom: '14px',
                  borderRadius: '10px',
                  background: aiMiq.latest_brief.score_delta > 0 
                    ? 'rgba(56, 239, 125, 0.08)' 
                    : 'rgba(244, 67, 54, 0.08)',
                  border: `1px solid ${aiMiq.latest_brief.score_delta > 0 ? 'rgba(56,239,125,0.2)' : 'rgba(244,67,54,0.2)'}`
                }}>
                  <span style={{ fontSize: '20px' }}>{aiMiq.latest_brief.score_delta > 0 ? '📈' : '📉'}</span>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: aiMiq.latest_brief.score_delta > 0 ? '#38ef7d' : '#f44336'
                  }}>
                    MIQ {aiMiq.latest_brief.score_delta > 0 ? '+' : ''}{aiMiq.latest_brief.score_delta} this week
                  </span>
                  {aiMiq.latest_brief.score_after && (
                    <span style={{ fontSize: '12px', color: '#999', marginLeft: '4px' }}>
                      (now {aiMiq.latest_brief.score_after})
                    </span>
                  )}
                </div>
              )}

              <MIQIntelligenceBrief
                userId={userId}
                aiMiq={aiMiq}
                userProfile={userProfile}
                aiNutritionGoals={aiNutritionGoals}
                metabolicProfile={metabolicProfile}
                dailyCheckinData={dailyCheckinData}
                BACKEND_URL={BACKEND_URL}
                onShowToast={onShowToast}
                onOpenSmartGoals={onOpenSmartGoals}
                setShowDailyCheckin={setShowDailyCheckin}
              />
            </div>
          )}
        </div>
      )}

      {/* Weekly Brief Locked Card - Post-Day-30 for Non-Genius */}
      {calibrationComplete && !isGeniusPlaite && (
        <div 
          style={{
            background: 'rgba(118, 75, 162, 0.06)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
            border: '1px solid rgba(118, 75, 162, 0.15)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <span style={{ fontSize: '16px' }}>🔒</span>
            <div>
              <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                Weekly MetaBolicIQ Intelligence Brief
              </span>
            </div>
          </div>
          
          <p style={{ 
            margin: '0 0 12px 0', 
            fontSize: '12px', 
            color: 'var(--text-secondary)',
            lineHeight: '1.5'
          }}>
            Your MIQ baseline is now complete. Upgrade to Genius Plaite to unlock your Weekly MetaBolicIQ Intelligence Brief and full metabolic intelligence guidance.
          </p>
          
          <button 
            onClick={() => {
              trackLockedCtaClicked({
                feature: 'miq_weekly_brief',
                tier: normalizeTier(userTier),
                source_view: 'miq_tab',
                reason: 'tier_locked',
                day_in_trial: calibrationDay || null
              });
              showUpgrade && showUpgrade('genius');
            }}
            style={{
              width: '100%',
              padding: '10px',
              background: 'linear-gradient(135deg, #9333ea 0%, #667eea 100%)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: '600',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Upgrade to Genius Plaite
          </button>
        </div>
      )}

      {/* Pre-Calibration: Show Intelligence Brief preview for all users during calibration */}
      {isCalibrating && calibrationStarted && (
        <div 
          onClick={() => setIntelligenceBriefExpanded(!intelligenceBriefExpanded)}
          style={{
            background: 'rgba(118, 75, 162, 0.06)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
            cursor: 'pointer',
            border: '1px solid rgba(118, 75, 162, 0.15)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px' }}>🧠</span>
              <div>
                <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  MIQ Intelligence Brief
                </span>
                <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Read how MIQ is interpreting your data
                </p>
              </div>
            </div>
            <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{intelligenceBriefExpanded ? '▲' : '▼'}</span>
          </div>
          
          {/* Expanded: Show Intelligence Brief */}
          {intelligenceBriefExpanded && (
            <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: '1px solid rgba(118, 75, 162, 0.15)' }}
                 onClick={(e) => e.stopPropagation()}>
              <MIQIntelligenceBrief
                userId={userId}
                aiMiq={aiMiq}
                userProfile={userProfile}
                aiNutritionGoals={aiNutritionGoals}
                metabolicProfile={metabolicProfile}
                dailyCheckinData={dailyCheckinData}
                BACKEND_URL={BACKEND_URL}
                onShowToast={onShowToast}
                onOpenSmartGoals={onOpenSmartGoals}
                setShowDailyCheckin={setShowDailyCheckin}
              />
            </div>
          )}
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* 4️⃣ ACCELERATE YOUR PROGRESS (PRICING - LAST SECTION) */}
      {/* Optional multiplier - "I'm doing well, how do I go faster?" */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '600' }}>
          🚀 Accelerate Your Progress
        </h3>
        <p style={{ margin: '0 0 16px 0', fontSize: '11px', color: 'var(--text-secondary)' }}>
          You&apos;re doing well — here&apos;s how to go faster
        </p>
        
        {/* $4.99 - Smart Plaite */}
        <div style={{ 
          marginBottom: '12px',
          padding: '12px',
          background: checkSmartOrHigher(userTier) ? 'rgba(56, 239, 125, 0.08)' : 'rgba(255,255,255,0.03)',
          borderRadius: '10px',
          border: checkSmartOrHigher(userTier) ? '1px solid rgba(56, 239, 125, 0.2)' : '1px solid rgba(255,255,255,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#38ef7d' }} />
              <span style={{ fontSize: '12px', fontWeight: '600', color: '#38ef7d' }}>$4.99 — Smart Plaite</span>
            </div>
            {!checkSmartOrHigher(userTier) && (
              <button 
                onClick={() => showUpgrade && showUpgrade('smart')}
                style={{
                  background: 'linear-gradient(135deg, #38ef7d 0%, #11998e 100%)',
                  border: 'none', padding: '4px 10px', borderRadius: '12px',
                  fontSize: '10px', fontWeight: '600', color: 'white', cursor: 'pointer'
                }}
              >
                Unlock
              </button>
            )}
          </div>
          <div style={{ marginTop: '8px', paddingLeft: '16px' }}>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>🎯 SmartGoals • ☀️ Daily Check-ins • 📊 Micronutrients • 📷 Barcode</div>
          </div>
        </div>
        
        {/* $9.99 - Genius Plaite - IMPRESSIVE PRESENTATION */}
        <div style={{ 
          padding: '14px',
          background: isGeniusTier(userTier)
            ? 'linear-gradient(135deg, rgba(147, 51, 234, 0.15) 0%, rgba(102, 126, 234, 0.1) 100%)' 
            : 'linear-gradient(135deg, rgba(147, 51, 234, 0.08) 0%, rgba(102, 126, 234, 0.05) 100%)',
          borderRadius: '12px',
          border: isGeniusTier(userTier) ? '2px solid rgba(147, 51, 234, 0.4)' : '1px solid rgba(147, 51, 234, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '18px' }}>🧠</span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#9333ea' }}>Genius Plaite</span>
              </div>
              <p style={{ margin: '4px 0 0 16px', fontSize: '11px', color: 'var(--text-primary)', fontWeight: '500' }}>
                Your AI Nutrition Coach
              </p>
            </div>
            <span style={{ 
              background: 'linear-gradient(135deg, #9333ea 0%, #667eea 100%)',
              color: 'white',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '11px',
              fontWeight: '700',
              boxShadow: '0 2px 8px rgba(147, 51, 234, 0.3)'
            }}>$9.99/mo</span>
          </div>
          
          {/* Compelling Value Proposition */}
          <div style={{
            background: 'rgba(147, 51, 234, 0.1)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '12px'
          }}>
            <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
              Personalized guidance that adapts to your body and habits in real time. Goes beyond tracking — tells you what to do next.
            </p>
          </div>
          
          {/* Features Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '6px',
            marginBottom: '10px'
          }}>
            <div style={{ fontSize: '10px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>🍽️</span> SmartMeals
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>🔄</span> SmartSwaps
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>📊</span> Weekly Brief
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>🧠</span> MIQ Profile
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>🎯</span> SmartGoals
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>🧠</span> Health Intelligence (MIQ)
            </div>
          </div>
          
          <p style={{ 
            margin: '0 0 10px 0', 
            fontSize: '10px', 
            color: '#9333ea', 
            fontStyle: 'italic',
            textAlign: 'center'
          }}>
            + Everything in Smart Plaite
          </p>
          
          {!isGeniusTier(userTier) && (
            <button 
              onClick={() => showUpgrade && showUpgrade('genius')}
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #9333ea 0%, #667eea 100%)',
                border: 'none', 
                padding: '10px 16px', 
                borderRadius: '8px',
                fontSize: '12px', 
                fontWeight: '600', 
                color: 'white', 
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)'
              }}
            >
              Unlock Full Metabolic Intelligence
            </button>
          )}
          
          {isGeniusTier(userTier) && (
            <div style={{ 
              textAlign: 'center', 
              padding: '8px', 
              background: 'rgba(56, 239, 125, 0.1)', 
              borderRadius: '6px',
              fontSize: '11px',
              color: '#38ef7d',
              fontWeight: '600'
            }}>
              ✓ Active
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* PERSISTENT FOOTER DISCLAIMER */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      <div style={{
        padding: '12px 16px',
        background: 'rgba(102, 126, 234, 0.05)',
        borderRadius: '10px',
        marginBottom: '16px',
        textAlign: 'center'
      }}>
        <p style={{ 
          margin: 0, 
          fontSize: '10px', 
          color: 'var(--text-secondary)',
          lineHeight: '1.5'
        }}>
          Weekly MetaBolicIQ Intelligence Briefs begin after MIQ calibration and are available to Genius Plaite members.
        </p>
        <p style={{ 
          margin: '6px 0 0 0', 
          fontSize: '9px', 
          color: 'var(--text-secondary)',
          fontStyle: 'italic'
        }}>
          MIQ Intelligence outputs require approximately 30 days of learning data for accuracy.
        </p>
      </div>

      {/* Legal disclaimer */}
      <p style={{ fontSize: '10px', color: 'var(--text-secondary)', textAlign: 'center', marginBottom: '20px' }}>
        <a href="#" onClick={(e) => { e.preventDefault(); setCurrentView && setCurrentView('disclaimer'); }} style={{ color: '#667eea' }}>
          Legal & Medical Disclaimer
        </a>
        {' • '}
        AI-Powered Nutrition
      </p>

      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      {/* HIDDEN SECTIONS - Removed from main view for clarity */}
      {/* These sections are moved to collapsed views or removed entirely */}
      {/* ═══════════════════════════════════════════════════════════════════════════════ */}
      
      {/* Metabolic Body Profile Explanation Modal - Keep for tap interactions */}
      <MetabolicBodyProfileExplanation 
        isOpen={showMetabolicExplanation}
        onClose={() => setShowMetabolicExplanation(false)}
        hasAncestry={hasAncestry}
        userTier={userTier}
      />
    </div>
  );
};

/**
 * MIQConfidenceFactors - Progress checklist for MIQ confidence
 * CLICKABLE INPUT PROGRESSION - Each row routes to exact missing input
 * Uses CONFIDENCE_FACTOR_CONFIG for weights (shared with ConfidenceTracker)
 * 
 * NEW: SmartMeals and Smart Hub factors show actual backend values
 */
const MIQConfidenceFactors = ({
  userProfile,
  aiNutritionGoals,
  metabolicProfile,
  totalMealsLogged,
  dailyCheckinContribution,
  // Handler props for clickable rows
  onProfileSetup,
  onAssessment,
  onMealLogging,
  onDailyCheckin,
  // NEW: SmartMeals & Cookbook data from backend
  aiMiq,
  userTier,
  showUpgrade,
  setShowMealPlanner
}) => {
  // Check if ancestry is provided
  const hasAncestry = !!(aiNutritionGoals?.intake_data?.ancestry);
  const ancestryValue = aiNutritionGoals?.intake_data?.ancestry;
  
  // Check if work schedule is provided
  const hasLifestyleSchedule = !!(aiNutritionGoals?.intake_data?.work_schedule_type || userProfile?.work_schedule_type);
  const workScheduleValue = aiNutritionGoals?.intake_data?.work_schedule_type || userProfile?.work_schedule_type;
  
  // State for expanded helper text
  const [ancestryHelperExpanded, setAncestryHelperExpanded] = useState(false);
  
  // NEW: SmartMeals and Smart Hub data from backend
  const smartMealsLogged = aiMiq?.confidence?.smart_meals_logged || 0;
  const savedRecipesLogged = aiMiq?.confidence?.saved_recipes_logged || 0;
  const smartMealsFactor = aiMiq?.confidence?.factors?.smart_meals || 0;
  const savedRecipesFactor = aiMiq?.confidence?.factors?.saved_recipes || 0;
  
  // NEW: Factor weights from backend (as percentages)
  const factorsWeights = aiMiq?.confidence?.factors_weights || {};
  const smartMealsWeight = factorsWeights.smart_meals || 10;  // Reduced from 13/15 to 10
  const savedRecipesWeight = factorsWeights.saved_recipes || 5;
  
  // Tier checks - using centralized utilities
  const isGeniusPlaite = isGeniusTier(userTier);
  const isSmartOrHigher = checkSmartOrHigher(userTier);
  
  const { FACTORS } = CONFIDENCE_FACTOR_CONFIG;
  
  const factors = [
    { 
      name: 'Profile Setup', 
      complete: !!userProfile?.height_cm, 
      weight: FACTORS.PROFILE.weight,
      onClick: onProfileSetup,
      hint: 'Set up your basic profile'
    },
    { 
      name: 'SmartGoals Setup', 
      complete: aiNutritionGoals?.intake_complete, 
      weight: FACTORS.ASSESSMENT.weight,
      onClick: onAssessment,
      hint: 'Complete SmartGoals setup'
    },
    { 
      name: 'Meal Logging', 
      complete: totalMealsLogged >= FACTORS.MEALS.threshold, 
      weight: FACTORS.MEALS.weight, 
      progress: Math.min(totalMealsLogged / FACTORS.MEALS.threshold * 100, 100),
      onClick: onMealLogging,
      hint: `Log ${Math.max(0, FACTORS.MEALS.threshold - totalMealsLogged)} more meals`
    },
    // v1.1 - Daily Check-ins now has sub-factors
    { 
      name: 'Daily Check-Ins Completed', 
      complete: dailyCheckinContribution?.checkin_completed || false, 
      weight: 9, // 9% for completion
      progress: dailyCheckinContribution?.checkin_completed ? 100 : 0,
      onClick: onDailyCheckin,
      hint: dailyCheckinContribution?.checkin_completed ? 'Checked in today' : 'Complete daily check-in',
      isSubFactor: false
    },
    { 
      name: 'Sleep Source Verified', 
      complete: dailyCheckinContribution?.sleep_verified || false, 
      weight: 3, // 3% bonus for verified sleep
      progress: dailyCheckinContribution?.sleep_verified ? 100 : 0,
      onClick: onDailyCheckin,
      hint: dailyCheckinContribution?.sleep_verified ? 'Verified from wearable' : 'Use wearable data for +3%',
      isSubFactor: true
    },
    { 
      name: 'Steps Source Verified', 
      complete: dailyCheckinContribution?.steps_verified || false, 
      weight: 3, // 3% bonus for verified steps
      progress: dailyCheckinContribution?.steps_verified ? 100 : 0,
      onClick: onDailyCheckin,
      hint: dailyCheckinContribution?.steps_verified ? 'Verified from wearable' : 'Use wearable data for +3%',
      isSubFactor: true
    },
    { 
      name: '💧 Hydration Goal Met', 
      complete: dailyCheckinContribution?.hydration_goal_met || false, 
      weight: 3, // 3% bonus for meeting hydration goal
      progress: dailyCheckinContribution?.hydration_goal_met ? 100 : 0,
      onClick: onDailyCheckin,
      hint: dailyCheckinContribution?.hydration_goal_met 
        ? `${(dailyCheckinContribution?.hydration_ml / 1000).toFixed(1)}L logged ✓` 
        : `Hit your ${(dailyCheckinContribution?.hydration_goal_ml / 1000 || 2.5).toFixed(1)}L goal for +3%`,
      isSubFactor: true,
      isHydration: true
    },
    { 
      name: 'Metabolic Body Profile', 
      complete: !!(userProfile?.body_composition?.body_type || metabolicProfile?.somatotype), 
      weight: FACTORS.METABOLIC_BODY.weight,
      onClick: null, // Removed camera-based trigger - profile is inferred from behavior
      hint: 'Inferred from your goals & behavior'
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // NEW: SmartMeals Factor (Genius Plaite exclusive)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      name: '🍽️ SmartMeals',
      complete: smartMealsLogged >= 3,
      weight: smartMealsWeight, // Dynamic from backend
      progress: Math.min(100, (smartMealsLogged / 3) * 100),
      onClick: isGeniusPlaite ? setShowMealPlanner : () => showUpgrade('smart_meals'),
      hint: smartMealsLogged >= 3 
        ? `${smartMealsLogged} SmartMeals logged ✓` 
        : `SmartMeals logged: ${smartMealsLogged} (Goal: 3+)`,
      isSmartMeals: true,
      isLocked: !isGeniusPlaite,
      count: smartMealsLogged,
      goal: 3,
      factorValue: smartMealsFactor,
      badge: `Fastest MIQ Boost · +${smartMealsWeight}%`
    },
    // ═══════════════════════════════════════════════════════════════════════════
    // Smart Swaps Factor (Genius Plaite feature - access gated at entry)
    // ═══════════════════════════════════════════════════════════════════════════
    {
      name: '⭐ Smart Swaps',
      complete: savedRecipesLogged >= 5,
      weight: savedRecipesWeight, // Dynamic from backend
      progress: Math.min(100, (savedRecipesLogged / 5) * 100),
      onClick: null, // No internal gating - access is at Smart Hub entry
      hint: savedRecipesLogged >= 5 
        ? `${savedRecipesLogged} Smart Swaps logged ✓` 
        : `Smart Swaps logged: ${savedRecipesLogged} (Goal: 5+)`,
      isCookbook: true,
      isLocked: false, // Removed internal tier lock - Smart Hub access is gated at entry
      count: savedRecipesLogged,
      goal: 5,
      factorValue: savedRecipesFactor
    },
    {
      name: '🧬 Ancestry Context',
      complete: hasAncestry,
      weight: FACTORS.ANCESTRY.weight,
      onClick: null, // Collected via assessment, no direct CTA
      hint: hasAncestry ? `Provided: ${ancestryValue}` : 'Not provided',
      isAncestry: true // Special flag for custom rendering
    },
    {
      name: '🕐 Lifestyle Schedule',
      complete: hasLifestyleSchedule,
      weight: FACTORS.LIFESTYLE_SCHEDULE.weight,
      onClick: null, // Collected via assessment, no direct CTA
      hint: hasLifestyleSchedule 
        ? `Schedule: ${workScheduleValue?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}` 
        : 'Not provided',
      isLifestyleSchedule: true // Special flag for custom rendering
    }
  ];

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600' }}>
        🎯 MIQ Confidence Score
      </h3>
      <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
        Tap any incomplete factor to increase MIQ Confidence
      </p>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {factors.map((factor, i) => (
          <div key={i}>
            <div 
              onClick={
                factor.isLocked 
                  ? factor.onClick 
                  : (!factor.complete && factor.onClick 
                      ? factor.onClick 
                      : (factor.isAncestry ? () => setAncestryHelperExpanded(!ancestryHelperExpanded) : undefined))
              }
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                cursor: factor.isLocked || (!factor.complete && factor.onClick) || factor.isAncestry ? 'pointer' : 'default',
                padding: factor.isSubFactor ? '6px 10px 6px 28px' : '8px 10px',
                borderRadius: '8px',
                background: factor.isLocked 
                  ? 'rgba(255, 193, 7, 0.05)' 
                  : (!factor.complete && factor.onClick ? 'rgba(102, 126, 234, 0.05)' : 'transparent'),
                border: factor.isLocked 
                  ? '1px solid rgba(255, 193, 7, 0.2)' 
                  : (!factor.complete && factor.onClick ? '1px solid rgba(102, 126, 234, 0.15)' : '1px solid transparent'),
                transition: 'all 0.2s ease',
                marginTop: factor.isSubFactor ? '-6px' : '0'
              }}
            >
              {/* Icon: Lock for locked, checkmark for complete, circle for incomplete */}
              <span style={{ 
                fontSize: factor.isSubFactor ? '12px' : '14px', 
                color: factor.isLocked ? '#FFC107' : (factor.complete ? '#38ef7d' : 'var(--text-secondary)') 
              }}>
                {factor.isLocked ? '🔒' : (factor.isAncestry ? (factor.complete ? '✅' : '○') : (factor.complete ? '✅' : '○'))}
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <span style={{ 
                      fontSize: factor.isSubFactor ? '11px' : '12px', 
                      color: factor.isLocked ? 'rgba(255, 193, 7, 0.9)' : (factor.complete ? 'var(--text-primary)' : 'var(--text-secondary)'),
                      fontWeight: factor.isLocked || !factor.complete ? '500' : '400',
                      fontStyle: factor.isSubFactor ? 'italic' : 'normal'
                    }}>
                      {factor.isSubFactor ? '↳ ' : ''}{factor.name}
                    </span>
                    {factor.isLocked && (
                      <span style={{ fontSize: '9px', color: '#FFC107' }}>GENIUS</span>
                    )}
                    {/* Dynamic badge for SmartMeals - "Fastest MIQ Boost · +{weight}%" */}
                    {factor.badge && (
                      <span style={{
                        fontSize: '8px',
                        padding: '2px 6px',
                        borderRadius: '8px',
                        background: factor.isLocked 
                          ? 'rgba(255, 193, 7, 0.15)' 
                          : 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                        border: factor.isLocked 
                          ? '1px solid rgba(255, 193, 7, 0.3)'
                          : '1px solid rgba(102, 126, 234, 0.3)',
                        color: factor.isLocked ? '#FFC107' : '#667eea',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        {factor.badge}
                      </span>
                    )}
                  </div>
                  <span style={{ 
                    fontSize: '10px', 
                    color: factor.isLocked ? 'rgba(255, 193, 7, 0.7)' : (factor.complete ? '#38ef7d' : 'var(--text-secondary)') 
                  }}>
                    +{factor.weight}%
                  </span>
                </div>
                
                {/* SmartMeals/Smart Hub specific hint with count */}
                {(factor.isSmartMeals || factor.isCookbook) && (
                  <p style={{ 
                    margin: '0 0 4px 0', 
                    fontSize: '10px', 
                    color: factor.isLocked ? '#FFC107' : (factor.complete ? '#38ef7d' : '#667eea')
                  }}>
                    {factor.isLocked 
                      ? `→ Upgrade to Genius Plaite to unlock`
                      : `→ ${factor.hint}`
                    }
                  </p>
                )}
                
                {/* Regular hint for non-ancestry, non-SmartMeals/Smart Hub factors */}
                {!factor.isAncestry && !factor.isSmartMeals && !factor.isCookbook && !factor.complete && factor.hint && (
                  <p style={{ margin: '0 0 4px 0', fontSize: '10px', color: '#667eea' }}>
                    → {factor.hint}
                  </p>
                )}
                
                {/* Ancestry-specific status */}
                {factor.isAncestry && (
                  <p style={{ 
                    margin: '0 0 4px 0', 
                    fontSize: '10px', 
                    color: factor.complete ? 'var(--text-secondary)' : 'rgba(255,255,255,0.5)',
                    fontStyle: factor.complete ? 'normal' : 'italic'
                  }}>
                    {factor.hint}
                  </p>
                )}
                
                {/* Progress bar for non-ancestry factors */}
                {!factor.isAncestry && (
                  <div style={{ height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{
                      width: factor.isLocked ? '0%' : (factor.complete ? '100%' : factor.progress ? `${factor.progress}%` : '0%'),
                      height: '100%',
                      background: factor.complete ? '#38ef7d' : 'linear-gradient(90deg, #667eea, #764ba2)',
                      borderRadius: '2px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                )}
              </div>
              
              {/* Arrow indicator */}
              {factor.isLocked && (
                <span style={{ fontSize: '14px', color: '#FFC107' }}>→</span>
              )}
              {!factor.isLocked && !factor.complete && factor.onClick && (
                <span style={{ fontSize: '14px', color: '#667eea' }}>→</span>
              )}
              {factor.isAncestry && (
                <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>
                  {ancestryHelperExpanded ? '▲' : '▼'}
                </span>
              )}
            </div>
            
            {/* Ancestry Expanded Helper Text */}
            {factor.isAncestry && ancestryHelperExpanded && (
              <div style={{
                marginTop: '8px',
                marginLeft: '34px',
                padding: '12px',
                background: 'rgba(102, 126, 234, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(102, 126, 234, 0.1)'
              }}>
                <p style={{ 
                  margin: '0 0 8px 0', 
                  fontSize: '11px', 
                  color: 'var(--text-secondary)',
                  lineHeight: '1.5'
                }}>
                  Providing ancestry context improves MIQ personalization. Population-level research shows certain ancestry groups can have different metabolic patterns. MIQ uses this as a context signal for supportive lifestyle guidance.
                </p>
                {/* Disclaimer - verbatim as required */}
                <p style={{ 
                  margin: 0, 
                  fontSize: '10px', 
                  color: 'rgba(255, 200, 100, 0.9)',
                  fontStyle: 'italic',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '4px'
                }}>
                  <span>⚠️</span>
                  <span>Ancestry context is used for personalization support only and does not predict or diagnose medical conditions.</span>
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Collapsed helper text for ancestry (shown below list when not expanded) */}
      {!ancestryHelperExpanded && (
        <p style={{ 
          margin: '12px 0 0 0', 
          fontSize: '10px', 
          color: 'var(--text-muted, rgba(255,255,255,0.4))',
          fontStyle: 'italic'
        }}>
          🧬 Ancestry: Used to improve population-level nutrition interpretation.
        </p>
      )}
    </div>
  );
};

/**
 * MIQEvolutionFactors - Detailed breakdown of all MIQ data sources
 */
const MIQEvolutionFactors = ({
  userProfile,
  aiNutritionGoals,
  metabolicProfile,
  dailyCheckinContribution,
  dailyCheckinData,
  todayMeals,
  totalMealsLogged
}) => {
  const categories = [
    {
      icon: '🏋️',
      title: 'Body & Metabolism',
      factors: [
        { label: 'Height/Weight', value: userProfile?.height_cm && userProfile?.weight_kg },
        { label: 'Age & Gender', value: userProfile?.age && userProfile?.gender },
        { label: 'Metabolic Body Profile', value: userProfile?.body_composition?.body_type || metabolicProfile?.somatotype },
        { label: 'Body Fat %', value: userProfile?.body_composition?.body_fat_percentage },
        { label: 'Activity Level', value: userProfile?.activity_level },
        { label: 'BMR Calculated', value: userProfile?.bmr }
      ]
    },
    {
      icon: '🎯',
      title: 'Goals & Preferences',
      factors: [
        { label: 'Primary Goal', value: aiNutritionGoals?.nutrition_goals?.goal_emphasis },
        { label: 'Goal Weight', value: userProfile?.goal_weight_kg },
        { label: 'Meal Frequency', value: aiNutritionGoals?.nutrition_goals?.meal_frequency },
        { label: 'Eating Window', value: aiNutritionGoals?.nutrition_goals?.eating_window },
        { label: 'Cuisine Preferences', value: aiNutritionGoals?.nutrition_goals?.cuisine_preferences?.length > 0 },
        { label: 'Dietary Restrictions', value: aiNutritionGoals?.nutrition_goals?.dietary_restrictions?.length > 0 }
      ]
    },
    {
      icon: '❤️',
      title: 'Health Context',
      factors: [
        { label: 'Medical Conditions', value: userProfile?.medical_conditions?.length > 0 },
        { label: 'Sleep Patterns', value: dailyCheckinData?.sleep_quality },
        { label: 'Stress Levels', value: dailyCheckinData?.stress_level },
        { label: 'Activity Tracking', value: dailyCheckinData?.activity_level },
        { label: '🧬 Ancestry Context', value: aiNutritionGoals?.intake_data?.ancestry },
        { label: 'Recovery Data', value: dailyCheckinContribution?.total_checkins > 0 }
      ]
    },
    {
      icon: '🍎',
      title: 'Nutrition Patterns',
      factors: [
        { label: 'Meals Logged', value: totalMealsLogged > 0, detail: totalMealsLogged },
        { label: 'Calorie Targets', value: userProfile?.daily_calories },
        { label: 'Macro Balance', value: userProfile?.macro_ratios },
        { label: 'Protein Intake', value: userProfile?.recommended_protein_g },
        { label: 'Meal Timing', value: todayMeals?.length > 1 },
        { label: 'Nutrient Variety', value: totalMealsLogged > 10 }
      ]
    }
  ];

  const activeDataSources = [
    userProfile?.height_cm, userProfile?.age, 
    userProfile?.body_composition?.body_type || metabolicProfile?.somatotype,
    aiNutritionGoals?.intake_complete, dailyCheckinContribution?.total_checkins > 0,
    totalMealsLogged > 0
  ].filter(Boolean).length;

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
        🧬 MIQ Evolution Factors
      </h3>
      <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
        These data points shape how MIQ understands and personalizes for you
      </p>
      
      {/* Factor Categories */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {categories.map((category, catIndex) => (
          <div key={catIndex}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ fontSize: '16px' }}>{category.icon}</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{category.title}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px', paddingLeft: '24px' }}>
              {category.factors.map((item, i) => (
                <div key={i} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '6px',
                  fontSize: '11px',
                  color: item.value ? 'var(--text-primary)' : 'var(--text-secondary)'
                }}>
                  <span style={{ color: item.value ? '#38ef7d' : 'rgba(255,255,255,0.3)' }}>
                    {item.value ? '●' : '○'}
                  </span>
                  {item.label}
                  {item.detail && <span style={{ color: '#667eea', fontSize: '10px' }}>({item.detail})</span>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Summary */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
        borderRadius: '10px',
        border: '1px solid rgba(102, 126, 234, 0.2)',
        textAlign: 'center'
      }}>
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
          MIQ currently learning from <strong style={{ color: '#667eea' }}>
            {activeDataSources}
          </strong> active data sources
        </p>
      </div>
    </div>
  );
};

// IFTimerCard removed - IF Timer now in standalone /components/if/IFTimerTab.js


export default MIQView;
